import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser, checkUserExists } from '../../services/authService';
import './DonateRegister.css';

// Country codes data
const countryCodes = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' }
];

const DonateRegister: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const countrySelectorRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+91',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [showCountrySelector, setShowCountrySelector] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  // Close country selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countrySelectorRef.current && !countrySelectorRef.current.contains(event.target as Node)) {
        setShowCountrySelector(false);
        setCountrySearch('');
      }
    };

    if (showCountrySelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountrySelector]);

  // Filter countries based on search
  const filteredCountries = countryCodes.filter(country =>
    country.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.code.includes(countrySearch)
  );

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const getPasswordStrength = (password: string): { strength: string; color: string } => {
    if (password.length === 0) return { strength: '', color: '' };
    if (password.length < 6) return { strength: 'Weak', color: '#dc2626' };
    if (password.length < 10) return { strength: 'Medium', color: '#f59e0b' };
    return { strength: 'Strong', color: '#10b981' };
  };

  const validateForm = async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      newErrors.firstName = 'First name should contain only letters';
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      newErrors.lastName = 'Last name should contain only letters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else {
      // Check if email already exists
      const exists = await checkUserExists(formData.email);
      if (exists) {
        newErrors.email = 'This email is already registered. Please login instead.';
      }
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      if (formData.countryCode === '+91') {
        if (!/^\d{10}$/.test(formData.phone)) {
          newErrors.phone = 'Please enter a valid 10-digit Indian phone number';
        }
      } else {
        if (!/^\d{5,}$/.test(formData.phone)) {
          newErrors.phone = 'Please enter a valid phone number';
        }
      }
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    // Confirm Password validation
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const result = await registerUser(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        formData.phone,
        formData.countryCode
      );

      if (result.success && result.user) {
        console.log('Registration successful:', result.userData);
        setRegisterSuccess(true);

        const userId = result.user.uid;

        // Redirect after 2 seconds with user ID
        setTimeout(() => {
          navigate(`/dashboard?id=${userId}`, { replace: true });
        }, 2000);
      } else {
        setErrors({
          email: result.error || 'Registration failed. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setErrors({
        email: error.message || 'An error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="donate-page">
      <section className="donate-section">
        <div className="donate-content">
          {/* Left Section - Image with Overlay */}
          <div className="donate-image-section">
            <div className="donate-image-container">
              <img
                src="/images/donate/donate.jpg"
                alt="Donation"
                className="donate-image"
              />
              <div className="donate-image-overlay">
                <div className="donate-welcome-text">
                  <div className="welcome-line">Register & Donate</div>
                  <div className="welcome-title">Create Your Account</div>
                  <div className="welcome-subtitle">
                    Track your donations, receive tax receipts, and manage recurring donations all in one place.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Form Card */}
          <div className="donate-form-section">
            <div className="donate-form-card">
              <div className="form-card-label">NEW DONOR REGISTRATION</div>
              <h1 className="form-card-title">Create Account</h1>
              <p className="form-card-subtitle">
                Join us in making a difference
              </p>

              {registerSuccess ? (
                <div className="register-success-message">
                  <div className="success-icon">✓</div>
                  <h2>Registration Successful!</h2>
                  <p>A verification email has been sent to your email address.</p>
                  <p className="redirect-note">Redirecting to your dashboard...</p>
                </div>
              ) : (
                <form className="donate-form" onSubmit={handleSubmit}>
                  {/* First Name & Last Name */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        First Name
                        <span className="required-asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="First name"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={errors.firstName ? 'error' : ''}
                        disabled={isLoading}
                      />
                      {errors.firstName && (
                        <div className="error-message">{errors.firstName}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        Last Name
                        <span className="required-asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Last name"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={errors.lastName ? 'error' : ''}
                        disabled={isLoading}
                      />
                      {errors.lastName && (
                        <div className="error-message">{errors.lastName}</div>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label>
                      Email Address
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={errors.email ? 'error' : ''}
                      disabled={isLoading}
                    />
                    {errors.email && (
                      <div className="error-message">{errors.email}</div>
                    )}
                  </div>

                  {/* Phone Number with Country Code */}
                  <div className="form-group">
                    <label>
                      Phone Number
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="phone-input-container">
                      <div className="country-selector" ref={countrySelectorRef}>
                        <button
                          type="button"
                          className="country-selector-button"
                          onClick={() => setShowCountrySelector(!showCountrySelector)}
                          disabled={isLoading}
                        >
                          <span className="selected-flag">
                            {countryCodes.find(c => c.code === formData.countryCode)?.flag}
                          </span>
                          <span className="selected-code">{formData.countryCode}</span>
                          <span className="dropdown-arrow">▼</span>
                        </button>

                        {showCountrySelector && (
                          <div className="country-dropdown">
                            <input
                              type="text"
                              className="country-search"
                              placeholder="Search country..."
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="country-list">
                              {filteredCountries.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  className="country-option"
                                  onClick={() => {
                                    handleInputChange('countryCode', country.code);
                                    // If switching to India and phone has more than 10 digits, truncate
                                    if (country.code === '+91' && formData.phone.length > 10) {
                                      handleInputChange('phone', formData.phone.slice(0, 10));
                                    }
                                    setShowCountrySelector(false);
                                    setCountrySearch('');
                                  }}
                                >
                                  <span className="country-flag">{country.flag}</span>
                                  <span className="country-name">{country.country}</span>
                                  <span className="country-code">{country.code}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <input
                        type="tel"
                        placeholder={formData.countryCode === '+91' ? '10-digit number' : 'Phone number'}
                        value={formData.phone}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');
                          // Limit to 10 digits for India
                          if (formData.countryCode === '+91' && value.length > 10) {
                            value = value.slice(0, 10);
                          }
                          handleInputChange('phone', value);
                        }}
                        maxLength={formData.countryCode === '+91' ? 10 : undefined}
                        className={`phone-input ${errors.phone ? 'error' : ''}`}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.phone && (
                      <div className="error-message">{errors.phone}</div>
                    )}
                  </div>

                  {/* Password */}
                  <div className="form-group">
                    <label>
                      Password
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={errors.password ? 'error' : ''}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {formData.password && (
                      <div className="password-strength">
                        <span>Password strength: </span>
                        <span style={{ color: passwordStrength.color, fontWeight: 700 }}>
                          {passwordStrength.strength}
                        </span>
                      </div>
                    )}
                    {errors.password && (
                      <div className="error-message">{errors.password}</div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="form-group">
                    <label>
                      Confirm Password
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Re-enter your password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={errors.confirmPassword ? 'error' : ''}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <div className="error-message">{errors.confirmPassword}</div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-next"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner"></span>
                          Creating Account...
                        </>
                      ) : (
                        'Create Account →'
                      )}
                    </button>
                  </div>

                  {/* Login Link */}
                  <div className="auth-switch-link">
                    <p>
                      Already have an account?
                      <Link to="/login"> Sign in here</Link>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonateRegister;
